import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LeadLinkQR } from "@/components/LeadLinkQR";
import { toast } from "sonner";
import { Copy, MapPin, Sparkles, Users } from "lucide-react";

function LinkRow({ item, icon: Icon }) {
  const url = `${window.location.origin}/apply/${item.token}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Long-press the link to copy it manually"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b last:border-0" data-testid={`reflink-${item.token}`}>
      <div className="w-9 h-9 rounded-lg bg-[#0A192F] text-emerald-400 flex items-center justify-center shrink-0"><Icon size={16} /></div>
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-slate-800">{item.label}</div>
        <div className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11} />{item.venue || "—"}{item.date ? ` · ${item.date}` : ""}</div>
      </div>
      <div className="text-xs font-medium bg-emerald-50 text-emerald-700 rounded-full px-2.5 py-1 flex items-center gap-1"><Users size={12} />{item.leads} leads</div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={copy} data-testid={`reflink-copy-${item.token}`}><Copy size={14} className="mr-1.5" />Copy</Button>
        <LeadLinkQR url={url} name={item.label} label="QR" testid={`reflink-qr-${item.token}`} />
      </div>
    </div>
  );
}

export default function ReferenceLinks() {
  const [data, setData] = useState({ branches: [], melas: [] });
  useEffect(() => { api.get("/reflinks").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="reflinks-page">
      <PageHeader title="Reference Links" subtitle="Share a lead-capture link per branch or per mela. Every registration is auto-tagged to that source and assigned round-robin to an RM at the venue." />
      <Card className="mb-6 overflow-hidden" data-testid="branch-links">
        <div className="px-5 py-3 bg-slate-50 border-b text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><MapPin size={13} />Branch-wise links</div>
        {data.branches.length === 0 ? (
          <div className="p-6 text-sm text-slate-400">No branches yet. Assign a Mela Venue to an RM or create a mela to generate branch links.</div>
        ) : data.branches.map((b) => <LinkRow key={b.token} item={b} icon={MapPin} />)}
      </Card>
      <Card className="overflow-hidden" data-testid="mela-links">
        <div className="px-5 py-3 bg-slate-50 border-b text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"><Sparkles size={13} />Mela-wise links</div>
        {data.melas.length === 0 ? (
          <div className="p-6 text-sm text-slate-400">No melas yet. Create a Loan Mela to generate its reference link.</div>
        ) : data.melas.map((m) => <LinkRow key={m.token} item={m} icon={Sparkles} />)}
      </Card>
    </div>
  );
}
